import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerIsBabackTeam } from "@/lib/rights/use-viewer-is-baback-team";
import { useCurrentReturnRequest } from "@/pages/(app)/returns/utils/current-return-request-context";
import { fetchClient } from "@babackapp/private-api-client/generated/client";
import type { ReturnRequestDto } from "@babackapp/private-api-client/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useState } from "react";
import { RepairShippingDialog } from "./repair-shipping-dialog";

// ─── Types for the non-generated endpoints ───────────────────────────────────

type ShippingRate = {
  handle: string;
  title: string;
  price: string;
};

type ExchangeOrderShippingInfo = {
  orderId: string;
  orderName: string;
  currentShippingLine: {
    title: string;
    code: string;
    source: string;
    carrierIdentifier: string | null;
  } | null;
  availableShippingRates: ShippingRate[];
};

type RepairShippingBody = {
  shippingLineTitle: string;
  shippingLinePrice: number;
  currencyCode: string;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getExchangeOrderId(returnRequest: ReturnRequestDto): string | null {
  for (const lineItem of returnRequest.line_items) {
    if (
      lineItem.resolution !== "exchange" &&
      lineItem.resolution !== "exchange-credit"
    ) {
      continue;
    }
    for (const info of lineItem.merchant_informations) {
      if (info.status === "ok" && info.result?.newOrderId) {
        return info.result.newOrderId;
      }
    }
  }
  return null;
}

type ShippingLineStatus =
  | { kind: "missing" }
  | { kind: "bad-source"; source: string }
  | { kind: "ok"; title: string };

function getShippingLineStatus(
  info: ExchangeOrderShippingInfo,
): ShippingLineStatus {
  if (!info.currentShippingLine) {
    return { kind: "missing" };
  }
  const { source, title, carrierIdentifier } = info.currentShippingLine;
  // source should be "shopify" for manual rates (no carrierIdentifier).
  // For carrier-service rates (carrierIdentifier set), any source value is expected.
  const isManualRateWithBadSource = !carrierIdentifier && source !== "shopify";
  if (isManualRateWithBadSource) {
    return { kind: "bad-source", source };
  }
  return { kind: "ok", title };
}

// ─── API hooks ────────────────────────────────────────────────────────────────

function useExchangeOrderShippingInfo(
  shopId: string,
  returnRequestId: string,
  enabled: boolean,
) {
  // fetchClient is typed against the generated schema — cast for non-generated endpoints
  const get = fetchClient.GET as unknown as (
    url: string,
    init: { params: { path: Record<string, string> } },
  ) => Promise<{ data?: ExchangeOrderShippingInfo; error?: unknown }>;

  return useQuery({
    queryKey: ["exchange-order-shipping-info", shopId, returnRequestId],
    enabled,
    queryFn: async () => {
      const { data, error } = await get(
        "/shops/{shopId}/return-requests/{returnRequestId}/exchange-order/shipping-info",
        { params: { path: { shopId, returnRequestId } } },
      );
      if (error) throw error;
      return data as ExchangeOrderShippingInfo;
    },
  });
}

function useRepairExchangeShipping(shopId: string, returnRequestId: string) {
  // fetchClient is typed against the generated schema — cast for non-generated endpoints
  const post = fetchClient.POST as unknown as (
    url: string,
    init: {
      params: { path: Record<string, string> };
      body: RepairShippingBody;
    },
  ) => Promise<{ data?: unknown; error?: unknown }>;

  return useMutation({
    mutationFn: async (body: RepairShippingBody) => {
      const { data, error } = await post(
        "/shops/{shopId}/return-requests/{returnRequestId}/exchange-order/repair-shipping",
        { params: { path: { shopId, returnRequestId } }, body },
      );
      if (error) throw error;
      return data;
    },
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ShippingStatusBadge({ status }: { status: ShippingLineStatus }) {
  if (status.kind === "missing") {
    return <span className="text-amber-600">⚠️ Aucune méthode configurée</span>;
  }
  if (status.kind === "bad-source") {
    return (
      <span className="text-amber-600">
        ⚠️ Source incorrecte (source: &quot;{status.source}&quot;)
      </span>
    );
  }
  return <span className="text-green-600">✅ {status.title}</span>;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ExchangeOrderCard() {
  const isBabackTeam = useViewerIsBabackTeam();
  const returnRequest = useCurrentReturnRequest();
  const { data: shop } = useCurrentShopSuspense();
  const [dialogOpen, setDialogOpen] = useState(false);

  const hasExchangeOrder = getExchangeOrderId(returnRequest) !== null;

  const {
    data: shippingInfo,
    isPending,
    isError,
  } = useExchangeOrderShippingInfo(
    shop.id,
    returnRequest.id,
    isBabackTeam && hasExchangeOrder,
  );

  const queryClient = useQueryClient();
  const repairMutation = useRepairExchangeShipping(shop.id, returnRequest.id);

  if (!isBabackTeam || !hasExchangeOrder) {
    return null;
  }

  const shippingLineStatus = shippingInfo
    ? getShippingLineStatus(shippingInfo)
    : null;

  const shouldShowRepairButton =
    shippingLineStatus !== null &&
    (shippingLineStatus.kind === "missing" ||
      shippingLineStatus.kind === "bad-source");

  const shopifyOrderUrl = shippingInfo
    ? `https://${shop.shopifyUrl}/admin/orders/${shippingInfo.orderId}`
    : null;

  return (
    <>
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-amber-900">
            Commande d&apos;échange
          </span>
          <span className="text-xs text-amber-700 bg-amber-100 rounded px-2 py-0.5">
            Baback CSM
          </span>
        </div>

        {isPending && (
          <p className="text-sm text-muted-foreground">Chargement…</p>
        )}

        {isError && (
          <p className="text-sm text-destructive">
            Erreur lors du chargement des informations de livraison.
          </p>
        )}

        {shippingInfo && (
          <div className="flex flex-col gap-2">
            {shopifyOrderUrl ? (
              <a
                href={shopifyOrderUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline"
              >
                {shippingInfo.orderName}
                <ExternalLink className="size-3" />
              </a>
            ) : (
              <span className="text-sm font-medium">
                {shippingInfo.orderName}
              </span>
            )}

            <div className="text-sm">
              <span className="text-muted-foreground mr-1">Livraison :</span>
              {shippingLineStatus && (
                <ShippingStatusBadge status={shippingLineStatus} />
              )}
            </div>

            {shouldShowRepairButton && (
              <button
                type="button"
                onClick={() => setDialogOpen(true)}
                className="mt-1 self-start rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-50 transition-colors"
              >
                Corriger la livraison
              </button>
            )}
          </div>
        )}
      </div>

      {shippingInfo && (
        <RepairShippingDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          availableRates={shippingInfo.availableShippingRates}
          currencyCode={returnRequest.currency.customerCurrency}
          repairMutation={repairMutation}
          onRepairSuccess={() => {
            void queryClient.invalidateQueries({
              queryKey: [
                "exchange-order-shipping-info",
                shop.id,
                returnRequest.id,
              ],
            });
          }}
        />
      )}
    </>
  );
}
